// Request deduplication utility to prevent duplicate API calls
// This is especially important in React StrictMode which causes double renders

type PendingRequest = {
  promise: Promise<any>;
  timestamp: number;
};

const pendingRequests = new Map<string, PendingRequest>();
const CACHE_DURATION = 5000; // 5 seconds cache

export function createDedupedRequest<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  keyGenerator: (...args: Parameters<T>) => string
): T {
  return (async (...args: Parameters<T>) => {
    const key = keyGenerator(...args);
    const now = Date.now();
    
    // Check if we have a pending or recent request
    const pending = pendingRequests.get(key);
    if (pending) {
      // If the request is still fresh, return the cached promise
      if (now - pending.timestamp < CACHE_DURATION) {
        console.log(`[Dedup] Returning cached request for key: ${key}`);
        return pending.promise;
      } else {
        // Clean up stale request
        pendingRequests.delete(key);
      }
    }
    
    // Create new request
    console.log(`[Dedup] Creating new request for key: ${key}`);
    const promise = fn(...args);
    
    // Store the promise
    pendingRequests.set(key, {
      promise,
      timestamp: now
    });
    
    // Clean up after completion
    promise.finally(() => {
      // Keep the result cached for a short time
      setTimeout(() => {
        const current = pendingRequests.get(key);
        if (current && current.promise === promise) {
          pendingRequests.delete(key);
        }
      }, CACHE_DURATION);
    });
    
    return promise;
  }) as T;
}

// Simple in-memory cache for static data
const staticCache = new Map<string, { data: any; timestamp: number }>();
const STATIC_CACHE_DURATION = 60000; // 1 minute for static data like categories/brands

export function createCachedRequest<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  keyGenerator: (...args: Parameters<T>) => string,
  cacheDuration: number = STATIC_CACHE_DURATION
): T {
  return (async (...args: Parameters<T>) => {
    const key = keyGenerator(...args);
    const now = Date.now();
    
    // Check cache
    const cached = staticCache.get(key);
    if (cached && now - cached.timestamp < cacheDuration) {
      console.log(`[Cache] Returning cached data for key: ${key}`);
      return Promise.resolve(cached.data);
    }
    
    // Make request using deduped function
    const dedupedFn = createDedupedRequest(fn, keyGenerator);
    const result = await dedupedFn(...args);
    
    // Cache the result
    staticCache.set(key, {
      data: result,
      timestamp: now
    });
    
    return result;
  }) as T;
}

// Clear all caches (useful for logout or refresh)
export function clearRequestCache() {
  pendingRequests.clear();
  staticCache.clear();
}